import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, TrendingUp, TrendingDown } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function StatBox({ label, value, subLabel }) {
  return (
    <div className="bg-astros-surface border border-astros-border p-3 text-center">
      <p className="text-xs text-astros-muted uppercase tracking-wider">{label}</p>
      <p className="font-bebas text-2xl text-white mt-1">{value ?? "-"}</p>
      {subLabel && <p className="text-xs text-astros-muted">{subLabel}</p>}
    </div>
  );
}

function PercentileBar({ label, value }) {
  if (value === null || value === undefined) return null;

  // Color: red < 25, orange 25-49, gray 50, blue 51-74, deep blue 75+
  const getColor = (v) => {
    if (v >= 90) return { bg: "bg-red-600", text: "text-red-100", ring: "ring-red-500" };
    if (v >= 75) return { bg: "bg-orange-500", text: "text-orange-100", ring: "ring-orange-400" };
    if (v >= 50) return { bg: "bg-gray-400", text: "text-gray-900", ring: "ring-gray-300" };
    if (v >= 25) return { bg: "bg-sky-500", text: "text-sky-100", ring: "ring-sky-400" };
    return { bg: "bg-blue-700", text: "text-blue-100", ring: "ring-blue-600" };
  };

  // For pitching stats where LOWER percentile on these is actually bad for them
  // Savant already flips these so higher = better from pitcher perspective
  // So we use the same color logic: high = elite (red)

  const colors = getColor(value);

  return (
    <div className="flex items-center gap-3 py-1.5" data-testid={`percentile-${label.replace(/[^a-zA-Z0-9]/g, '-')}`}>
      <span className="text-xs text-astros-muted w-24 text-right font-medium truncate">{label}</span>
      <div className="flex-1 h-5 bg-astros-border/40 relative overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${value}%` }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className={`h-full ${colors.bg} absolute left-0 top-0`}
        />
        {/* 50th percentile marker */}
        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-white/20 z-10"></div>
      </div>
      <span className={`text-xs font-bold w-10 text-center px-1.5 py-0.5 ${colors.bg} ${colors.text} ring-1 ${colors.ring}`}>
        {value}
      </span>
    </div>
  );
}

function SavantPercentiles({ percentiles }) {
  if (!percentiles || Object.keys(percentiles).length === 0) return null;

  const entries = Object.entries(percentiles);

  return (
    <div className="mb-8" data-testid="savant-percentiles">
      <div className="flex items-center gap-3 mb-4">
        <h2 className="font-bebas text-xl text-astros-orange tracking-wider">BASEBALL SAVANT PERCENTILE RANKINGS</h2>
        <span className="text-xs text-astros-muted bg-astros-surface border border-astros-border px-2 py-0.5">Statcast</span>
      </div>

      {/* Color legend */}
      <div className="flex items-center gap-4 mb-4 text-xs text-astros-muted">
        <div className="flex items-center gap-1"><div className="w-3 h-3 bg-blue-700"></div> Poor</div>
        <div className="flex items-center gap-1"><div className="w-3 h-3 bg-sky-500"></div> Below Avg</div>
        <div className="flex items-center gap-1"><div className="w-3 h-3 bg-gray-400"></div> Average</div>
        <div className="flex items-center gap-1"><div className="w-3 h-3 bg-orange-500"></div> Great</div>
        <div className="flex items-center gap-1"><div className="w-3 h-3 bg-red-600"></div> Elite</div>
      </div>

      <div className="bg-astros-surface border border-astros-border p-4">
        <div className="grid md:grid-cols-2 gap-x-8">
          {entries.map(([label, value]) => (
            <PercentileBar key={label} label={label} value={value} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function PlayerDetailPage() {
  const { playerId } = useParams();
  const navigate = useNavigate();
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        const res = await axios.get(`${API}/players/${playerId}/detail`);
        setPlayer(res.data);
      } catch (e) {
        console.error("Error fetching player detail:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [playerId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-astros-dark flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-astros-orange border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!player) {
    return (
      <div className="min-h-screen bg-astros-dark flex items-center justify-center">
        <p className="text-astros-muted">Player not found</p>
      </div>
    );
  }

  const isP = player.position_type === "pitcher";
  const s = player.season_stats || {};
  const prev = player.prev_season_stats || {};
  const last7 = player.last_7_games || {};
  const fg = player.fg_extras || {};

  return (
    <div className="min-h-screen bg-astros-dark" data-testid="player-detail-page">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Back button */}
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-astros-muted hover:text-astros-orange mb-6 transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft size={18} /> Back to Dashboard
        </button>

        {/* Player Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-start gap-6 mb-8"
        >
          <img
            src={player.photo_url}
            alt={player.name}
            className="w-32 h-32 object-cover bg-astros-navy border-2 border-astros-orange"
            onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1770228783704-32823e759c03?w=128&h=128&fit=crop"; }}
          />
          <div>
            <h1 className="font-bebas text-4xl sm:text-5xl text-white tracking-tight">
              {player.name}
            </h1>
            <div className="flex items-center gap-4 mt-1">
              <span className="text-astros-orange font-bebas text-2xl">#{player.jersey_number || "00"}</span>
              <span className="text-astros-muted text-lg">{player.position}</span>
            </div>
            {s.fwar !== null && s.fwar !== undefined && (
              <div className="mt-3 inline-flex items-center gap-2 bg-astros-surface border border-astros-border px-3 py-1.5">
                <span className="text-xs text-astros-muted uppercase tracking-wider">fWAR</span>
                <span className={`font-bebas text-xl ${s.fwar >= 0 ? "text-astros-success" : "text-astros-danger"}`}>
                  {s.fwar}
                </span>
              </div>
            )}
          </div>
        </motion.div>

        {/* Season Stats */}
        <div className="mb-8">
          <h2 className="font-bebas text-xl text-astros-orange tracking-wider mb-3">2026 SEASON STATS</h2>
          <div className={`grid ${isP ? "grid-cols-4 sm:grid-cols-8" : "grid-cols-4 sm:grid-cols-8"} gap-2`}>
            {isP ? (<>
              <StatBox label="W-L" value={`${s.wins || 0}-${s.losses || 0}`} />
              <StatBox label="ERA" value={s.era} />
              <StatBox label="FIP" value={s.fip} />
              <StatBox label="WHIP" value={s.whip} />
              <StatBox label="IP" value={s.innings_pitched} />
              <StatBox label="K" value={s.strikeouts} />
              <StatBox label="K/9" value={s.k_per_9} />
              <StatBox label="BB/9" value={s.bb_per_9} />
            </>) : (<>
              <StatBox label="AVG" value={s.avg} />
              <StatBox label="OBP" value={s.obp} />
              <StatBox label="SLG" value={s.slg} />
              <StatBox label="OPS" value={s.ops} />
              <StatBox label="HR" value={s.home_runs} />
              <StatBox label="RBI" value={s.rbi} />
              <StatBox label="BB" value={s.walks} />
              <StatBox label="K" value={s.strikeouts} />
            </>)}
          </div>
        </div>

        {/* Advanced Stats + FG Extras */}
        <div className="mb-8">
          <h2 className="font-bebas text-xl text-astros-orange tracking-wider mb-3">ADVANCED STATS</h2>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {isP ? (<>
              <StatBox label="BABIP" value={s.babip} />
              <StatBox label="BAA" value={s.avg_against} />
              <StatBox label="ER" value={s.earned_runs} />
              <StatBox label="SV" value={s.saves} />
              <StatBox label="HLD" value={s.holds} />
              <StatBox label="GS" value={s.games_started} />
            </>) : (<>
              <StatBox label="BABIP" value={s.babip} />
              <StatBox label="SB" value={s.stolen_bases} />
              <StatBox label="2B" value={s.doubles} />
              <StatBox label="3B" value={s.triples} />
              <StatBox label="GIDP" value={s.ground_into_dp} />
              <StatBox label="PA" value={s.plate_appearances} />
            </>)}
          </div>
          {(fg.wrc_plus || fg.barrel_pct || fg.hard_hit_pct) && (
            <div className="grid grid-cols-3 gap-2 mt-2">
              {fg.wrc_plus != null && <StatBox label="wRC+" value={fg.wrc_plus} subLabel="FanGraphs" />}
              {fg.barrel_pct != null && <StatBox label="Barrel%" value={`${fg.barrel_pct}%`} subLabel="FanGraphs" />}
              {fg.hard_hit_pct != null && <StatBox label="HardHit%" value={`${fg.hard_hit_pct}%`} subLabel="FanGraphs" />}
            </div>
          )}
        </div>

        {/* Baseball Savant Percentiles */}
        <SavantPercentiles percentiles={player.savant_percentiles} />

        {/* Last 7 Games + 2025 Comparison */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div>
            <h2 className="font-bebas text-xl text-astros-orange tracking-wider mb-3">LAST 7 GAMES</h2>
            <div className="grid grid-cols-4 gap-2">
              {isP ? (<>
                <StatBox label="ERA" value={last7.era} />
                <StatBox label="WHIP" value={last7.whip} />
                <StatBox label="K" value={last7.k} />
                <StatBox label="IP" value={last7.ip} />
              </>) : (<>
                <StatBox label="AVG" value={last7.avg} />
                <StatBox label="OPS" value={last7.ops} />
                <StatBox label="HR" value={last7.hr} />
                <StatBox label="RBI" value={last7.rbi} />
              </>)}
            </div>
          </div>
          <div>
            <h2 className="font-bebas text-xl text-astros-orange tracking-wider mb-3">2025 SEASON</h2>
            {Object.keys(prev).length > 0 ? (
              <div className="grid grid-cols-4 gap-2">
                {isP ? (<>
                  <StatBox label="ERA" value={prev.era} />
                  <StatBox label="WHIP" value={prev.whip} />
                  <StatBox label="K" value={prev.strikeouts} />
                  <StatBox label="IP" value={prev.innings_pitched} />
                </>) : (<>
                  <StatBox label="AVG" value={prev.avg} />
                  <StatBox label="OPS" value={prev.ops} />
                  <StatBox label="HR" value={prev.home_runs} />
                  <StatBox label="RBI" value={prev.rbi} />
                </>)}
              </div>
            ) : (
              <p className="text-astros-muted text-sm bg-astros-surface border border-astros-border p-4">No 2025 stats available</p>
            )}
          </div>
        </div>

        {/* Game Log */}
        <div>
          <h2 className="font-bebas text-xl text-astros-orange tracking-wider mb-3">GAME LOG (LAST 10)</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="game-log-table">
              <thead>
                <tr className="text-astros-muted border-b border-astros-border">
                  <th className="text-left py-2 pr-3 font-medium">Date</th>
                  <th className="text-left py-2 pr-3 font-medium">Opp</th>
                  {isP ? (<>
                    <th className="text-center px-2 py-2 font-medium">IP</th>
                    <th className="text-center px-2 py-2 font-medium">H</th>
                    <th className="text-center px-2 py-2 font-medium">ER</th>
                    <th className="text-center px-2 py-2 font-medium">K</th>
                    <th className="text-center px-2 py-2 font-medium">BB</th>
                  </>) : (<>
                    <th className="text-center px-2 py-2 font-medium">AB</th>
                    <th className="text-center px-2 py-2 font-medium">H</th>
                    <th className="text-center px-2 py-2 font-medium">HR</th>
                    <th className="text-center px-2 py-2 font-medium">RBI</th>
                    <th className="text-center px-2 py-2 font-medium">BB</th>
                    <th className="text-center px-2 py-2 font-medium">K</th>
                  </>)}
                </tr>
              </thead>
              <tbody>
                {player.game_log?.map((g, i) => (
                  <motion.tr
                    key={i}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: i * 0.03 }}
                    className="text-white border-b border-astros-border/30 hover:bg-astros-border/20"
                  >
                    <td className="py-2 pr-3">{g.date}</td>
                    <td className="py-2 pr-3 text-xs">
                      {g.is_home ? "vs" : "@"} {g.opponent?.replace("Houston Astros", "").trim().split(" ").pop()}
                    </td>
                    {isP ? (<>
                      <td className="text-center px-2 py-2">{g.ip}</td>
                      <td className="text-center px-2 py-2">{g.h}</td>
                      <td className={`text-center px-2 py-2 ${g.er > 0 ? "text-astros-danger" : "text-astros-success"}`}>{g.er}</td>
                      <td className={`text-center px-2 py-2 ${g.k > 0 ? "text-astros-success font-bold" : ""}`}>{g.k}</td>
                      <td className="text-center px-2 py-2">{g.bb}</td>
                    </>) : (<>
                      <td className="text-center px-2 py-2">{g.ab}</td>
                      <td className={`text-center px-2 py-2 ${g.h > 0 ? "text-astros-success font-bold" : ""}`}>{g.h}</td>
                      <td className={`text-center px-2 py-2 ${g.hr > 0 ? "text-astros-orange font-bold" : ""}`}>{g.hr}</td>
                      <td className={`text-center px-2 py-2 ${g.rbi > 0 ? "font-bold" : ""}`}>{g.rbi}</td>
                      <td className="text-center px-2 py-2">{g.bb}</td>
                      <td className="text-center px-2 py-2">{g.k}</td>
                    </>)}
                  </motion.tr>
                ))}
              </tbody>
            </table>
            {(!player.game_log || player.game_log.length === 0) && (
              <p className="text-astros-muted text-center py-4">No game log data</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
